import { mainLogger as log } from "@/app/logger"
import { Interval, Term } from "@/app/types"

// returns true if obj is null or undefined
// specifically 0 will return False, unlike JS check
export function isNull(obj: any): boolean {
  return obj === null || typeof obj === "undefined"
}
export function isNotNull(obj: any): boolean {
  return !isNull(obj)
}
export function isAnyNull(...objs: any): boolean {
  return objs.some((obj) => isNull(obj))
}

// see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/typeof
export function isString(obj: any): boolean {
  return typeof obj === "string"
}
export function isNotString(obj: any): boolean {
  return !isString(obj)
}
export function isStringEmpty(obj: any): boolean {
  return isString(obj) && (obj.length == 0 || (obj as string).trim() == "")
}
export function isStringNotEmpty(obj: any): boolean {
  return isString(obj) && (obj as string).trim() != ""
}

export function isNumber(obj: any): boolean {
  return typeof obj === "number"
}
export function isNotNumber(obj: any): boolean {
  return !isNumber(obj)
}

export function isBoolean(obj: any): boolean {
  return typeof obj === "boolean"
}
export function isNotBoolean(obj: any): boolean {
  return !isBoolean(obj)
}

export function isFunction(obj: any): boolean {
  return typeof obj === "function"
}
export function isNotFunction(obj: any): boolean {
  return !isFunction(obj)
}
export function isFunctionWithName(obj: any, name: string): boolean {
  return typeof obj === "function" && isString(name) && obj.name == name
}

export function isArray(obj: any): boolean {
  return Array.isArray(obj)
}
export function isNotArray(obj: any): boolean {
  return !isArray(obj)
}

export function isArrayWithNoItems(obj: any): boolean {
  return isArray(obj) && obj.length == 0
}
export function isArrayWithAtleast(obj: any, atLeast: number = 1): boolean {
  return isArray(obj) && obj.length >= atLeast
}
export function isArrayWithItems(obj: any): boolean {
  return isArrayWithAtleast(obj, 1)
}

export function isObject(obj: any): boolean {
  return isNotNull(obj) && typeof obj === "object"
}
export function isObjectWithProps(
  obj: any,
  requiredProps: string | string[] | null = null
): boolean {
  if (!isObject(obj)) return false
  if (isArrayWithItems(requiredProps))
    return (requiredProps as string[]).every((prop) => Object.hasOwn(obj, prop))
  if (isString(requiredProps))
    return Object.hasOwn(obj, requiredProps as string)
  if (isNull(requiredProps)) return Object.getOwnPropertyNames(obj).length > 0

  log.error(
    "Unexpected requiredProps in isObjectWithProps(). Expected:string[], Got:",
    requiredProps,
    "with obj:",
    obj
  )
  return false // whatever was required cant be checked so safe to return false
}

// if prop exists in obj, return it or else return null
export function get(prop: string, obj: any): any {
  return isString(prop) && isNotNull(obj) && prop in obj ? obj[prop] : null
}

// returns the params joined together with a space
// useful for merging conditional classNames together
export function join(...classes): string {
  return !Array.isArray(classes) ?
      ""
    : classes.filter((c) => isString(c)).join(" ")
}

// creates an internal "termId" to serve as key for the termsMapping
export function termId(
  isPeriodic: boolean,
  interval: Interval | string,
  key: string
): string {
  return `${isPeriodic ? "periodic" : "ranking"}-${interval}-${key}`
}

/**
 * Formats a date string into a human-readable format.
 * @param date - The date string to be formatted.
 * @returns A formatted date string in the format "Month, Year".
 */
export function formatDateMonthYear(date: string): string {
  const parsedDate = new Date(date)
  const formattedDate = parsedDate.toLocaleDateString("en-us", {
    year: "numeric",
    month: "long",
  })

  return formattedDate
}

/**
 * Formats a date string into a human-readable format.
 * @param date - The date string to be formatted.
 * @returns A formatted date string in the format "Month Day, Year".
 */
export function formatDateComplete(date: string): string {
  const parsedDate = new Date(date)
  const formattedDate = parsedDate.toLocaleDateString("en-us", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Europe/Paris",
  })

  return formattedDate
}

export function formatDateShort(date: string): string {
  const parsedDate = new Date(date)
  const formattedDate = parsedDate.toLocaleDateString("en-us", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "Europe/Paris",
  })

  return formattedDate
}

export function formatDateSlashed(dateString: string) {
  const date = new Date(dateString)

  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear()

  return `${month}/${day}/${year}`
}

/**
 * Get an 'As of' label from a term object.
 * @param term - The term object containing the 'asOfDate' property.
 * @returns A string representing the 'As of' label in the format "Month Day, Year".
 */
export function getAsOfLabel(term: Term): string {
  if (!term) return ""

  const asOfDate = new Date(term.asOfDate)

  const asOfLabel = `As of ${asOfDate.getUTCMonth() + 1}/${asOfDate.getUTCDate()}/${
    asOfDate.getUTCFullYear() % 100
  }`

  return asOfLabel
}
